// src/App.jsx
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import { Typography } from '@mui/material';
import './App.css';

import RouteScrollToTop from './components/RouteScrollToTop';

import LandingPage from './pages/WebView/Landingpage';
import AboutPage from './pages/WebView/About';
import Register from './pages/ARegister/Register';
import Login from './pages/BLogin/Login';
import StudentRegistration from './pages/ARegister/StudentRegister';
import ClubMemberRegistration from './pages/ARegister/ClubmemberRegister';
import StudentLogin from './pages/BLogin/StudentLogin';
import ClubMemberLogin from './pages/BLogin/ClubmemberLogin';
import Admin from './pages/BLogin/AdminLogin';
import ForgotPassword from './pages/BLogin/ForgotPassword';
import ResetPassword from './pages/BLogin/ResetPassword';
import AnnouncementsPage from './pages/Announcements/AnnouncementsPage';
import AdminAnnouncements from './pages/Admin/AdminAnnouncements';
import LegalPage from './pages/WebView/LegalPage';
import MarkdownLegalPage from './pages/WebView/MarkdownLegalPage';
import privacyPolicyUrl from './content/legal/privacy-policy.md';
import termsOfServiceUrl from './content/legal/terms-of-service.md';
import StudentProfile from './pages/CHomepages/Student';
import ClubMember from './pages/CHomepages/ClubMember';
import CollegeAdmin from './pages/CHomepages/CollegeAdmin';
import LostFound from './pages/LostFound/LostFound';
import Skills from './pages/Skills/Skills';
import Doubts from './pages/Doubts/Doubts';
import Events from './pages/Events/Events';
import EventDetailPage from "./pages/Events/EventDetailPage";
import EventManagement from "./pages/Events/EventManagement";
import Yourskills from './pages/Skills/Yourskills';
import Yourdoubts from './pages/Doubts/Yourdoubts';
import YourLostFound from './pages/LostFound/YourLostFound';
import AdminDashboard from './pages/Admin/AdminDashboard';
import ManageClubs from './pages/Admin/ManageClubs';
import AddClub from './pages/Admin/AddClub';
import EditClub from './pages/Admin/EditClub';
import ManageClubMembers from './pages/Admin/ManageClubMembers';
import ManageStudents from './pages/Admin/ManageStudents';
import AddMember from './pages/Admin/AddMember';
import AddStudent from './pages/Admin/AddStudent';
import ViewMember from './pages/Admin/ViewMember';

import RequireAuth from './components/RequireAuth';

// Attendance imports 
import TeacherDashboard from './pages/ZExtra/TeacherDashboard';
import StudentAttendance from './pages/ZExtra/StudentAttendance';

import MatchedItems from './pages/LostFound/MatchedItems';

// Know Your College
import KnowYourCollege from './pages/ZExtra/KnowYourCollege';
import LibraryManagementSystem from './pages/LibraryMananagementSystem/LibraryManagementSystem';

// Welcome page imports
import Welcome from './pages/ZExtra/Welcome';

  
// Simple inline NotFound page (replace with your own 404 page if available)
function NotFound() {
  const navigate = useNavigate();

  const canGoBack = (() => {
    try {
      const idx = window.history?.state?.idx;
      if (typeof idx === 'number') return idx > 0;
      return window.history.length > 1;
    } catch {
      return false;
    }
  })();

  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <h2>404 — Page not found</h2>
      <p>The page you requested doesn't exist.</p>
      <button
        type="button"
        onClick={() => (canGoBack ? navigate(-1) : navigate('/', { replace: true }))}
        style={{
          marginTop: 16,
          padding: '10px 14px',
          borderRadius: 10,
          border: '1px solid rgba(0,0,0,0.15)',
          background: 'white',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        Back
      </button>
    </div>
  );
}

function App() {
  return (
    <SnackbarProvider 
      maxSnack={3}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      classes={{
        containerAnchorOriginTopRight: 'app-snackbar-top-right',
      }}
    >
      <AuthProvider>
        <Router>
          <RouteScrollToTop />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/signup" element={<Register />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup/student" element={<StudentRegistration />} />
            <Route path="/signup/club-member" element={<ClubMemberRegistration />} />
            <Route path="/login/student" element={<StudentLogin />} />
            <Route path="/login/club-member" element={<ClubMemberLogin />} />
            <Route path="/login/admin" element={<Admin />} />

            {/* Password reset */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Backward-compatible alias for existing admin link */}
            <Route path="/admin/forgot-password" element={<ForgotPassword defaultRole="admin" />} />

            {/* Legal (used by footer on pre-login pages) */}
            <Route path="/privacy" element={<MarkdownLegalPage markdownUrl={privacyPolicyUrl} fallbackTitle="Privacy Policy" />} />
            <Route path="/terms" element={<MarkdownLegalPage markdownUrl={termsOfServiceUrl} fallbackTitle="Terms of Service" />} />
            <Route
              path="/accessibility"
              element={
                <LegalPage title="Accessibility" updatedAt="2026-02-08">
                  <Typography paragraph>
                    This is a placeholder accessibility page. Replace this content with your official accessibility statement.
                  </Typography>
                </LegalPage>
              }
            />

            <Route path="/library/*" element={<LibraryManagementSystem />} />

            {/* Homepages */}
            <Route path="/login/student/home" element={<RequireAuth><StudentProfile /></RequireAuth>} />
            {/* FIXED: use consistent hyphen in club-member route */}
            <Route path="/login/clubmember/home" element={<RequireAuth><ClubMember /></RequireAuth>} />

            {/* Welcome (post-login landing) */}
            <Route path="/student" element={<RequireAuth allowedRoles={['student']}><Navigate to="/student/welcome" replace /></RequireAuth>} />
            <Route path="/clubmember" element={<RequireAuth allowedRoles={['club_member']}><Navigate to="/clubmember/welcome" replace /></RequireAuth>} />
            <Route path="/student/welcome" element={<RequireAuth allowedRoles={['student']}><Welcome /></RequireAuth>} />
            <Route path="/clubmember/welcome" element={<RequireAuth allowedRoles={['club_member']}><Welcome /></RequireAuth>} />
            {/* Club member pages (full screen) */}
            <Route path="/clubmember/lostfound" element={<RequireAuth><LostFound /></RequireAuth>} />
            <Route path="/clubmember/skills" element={<RequireAuth><Skills /></RequireAuth>} />
            <Route path="/clubmember/doubts" element={<RequireAuth><Doubts /></RequireAuth>} />
            <Route path="/clubmember/events" element={<RequireAuth><Events /></RequireAuth>} />
            <Route path="/clubmember/event/:id" element={<RequireAuth><EventDetailPage /></RequireAuth>} />
            <Route path="/clubmember/know-your-college" element={<RequireAuth><KnowYourCollege /></RequireAuth>} />

            {/* Club member "My" pages (full screen) */}
            <Route path="/clubmember/yourlostfound" element={<RequireAuth><YourLostFound /></RequireAuth>} />
            <Route path="/clubmember/yourskills" element={<RequireAuth><Yourskills /></RequireAuth>} />
            <Route path="/clubmember/yourdoubts" element={<RequireAuth><Yourdoubts /></RequireAuth>} />
            <Route path="/clubmember/eventmanagement" element={<RequireAuth><EventManagement /></RequireAuth>} />
            <Route path="/login/admin/home" element={<RequireAuth><CollegeAdmin /></RequireAuth>} />

            {/* Announcements */}
            <Route path="/announcements" element={<RequireAuth><AnnouncementsPage /></RequireAuth>} />
            {/* Student pages (full screen) */}
            <Route path="/student/announcements" element={<RequireAuth><AnnouncementsPage /></RequireAuth>} />
            <Route path="/student/lostfound" element={<RequireAuth><LostFound /></RequireAuth>} />
            <Route path="/student/skills" element={<RequireAuth><Skills /></RequireAuth>} />
            <Route path="/student/doubts" element={<RequireAuth><Doubts /></RequireAuth>} />
            <Route path="/student/events" element={<RequireAuth><Events /></RequireAuth>} />
            <Route path="/student/event/:id" element={<RequireAuth><EventDetailPage /></RequireAuth>} />
            <Route path="/student/know-your-college" element={<RequireAuth><KnowYourCollege /></RequireAuth>} />

            {/* Student "My" pages (full screen) */}
            <Route path="/student/yourlostfound" element={<RequireAuth><YourLostFound /></RequireAuth>} />
            <Route path="/student/yourskills" element={<RequireAuth><Yourskills /></RequireAuth>} />
            <Route path="/student/yourdoubts" element={<RequireAuth><Yourdoubts /></RequireAuth>} />
            <Route
              path="/admin/announcements"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <AdminAnnouncements />
                </RequireAuth>
              }
            />

            <Route path="/lostfound" element={<RequireAuth><LostFound /></RequireAuth>} />
            <Route path="/skills" element={<RequireAuth><Skills /></RequireAuth>} />
            <Route path="/yourskills" element={<RequireAuth><Yourskills /></RequireAuth>} />
            <Route path="/yourdoubts" element={<RequireAuth><Yourdoubts /></RequireAuth>} />
            <Route path="/yourlostfound" element={<RequireAuth><YourLostFound /></RequireAuth>} />
            <Route path="/doubts" element={<RequireAuth><Doubts /></RequireAuth>} />

            {/* ✅ Events routing */}
            <Route path="/events" element={<RequireAuth><Events /></RequireAuth>} />
            <Route path="/event/:id" element={<RequireAuth><EventDetailPage /></RequireAuth>} />
            <Route path="/eventmanagement" element={<RequireAuth><EventManagement /></RequireAuth>} />

            
            <Route
              path="/admin/dashboard"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <AdminDashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/clubs"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <ManageClubs />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/students"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <ManageStudents />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/students/new"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <AddStudent />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/clubs/new"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <AddClub />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/clubs/:id/edit"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <EditClub />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/memberships"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <ManageClubMembers />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/memberships/new"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <AddMember />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/memberships/:id/edit"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <ViewMember />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/memberships/:id"
              element={
                <RequireAuth allowedRoles={['admin', 'college_admin']}>
                  <ViewMember />
                </RequireAuth>
              }
            />

            {/* Attendance Routes */}
            <Route path="/teacher" element={<RequireAuth><TeacherDashboard /></RequireAuth>} />
            <Route path="/student/attendance" element={<RequireAuth><StudentAttendance /></RequireAuth>} />

            <Route path="/matcheditems" element={<RequireAuth><MatchedItems /></RequireAuth>} />

            {/* Know Your College */}
            <Route path="/know-your-college" element={<RequireAuth><KnowYourCollege /></RequireAuth>} />

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </SnackbarProvider>
  );
}

export default App;
